import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDealValue } from "@/utils/formatDealValue";
import { formatFullValue } from "@/utils/formatFullValue";
import { Button } from "@/components/ui/button";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Plus, Search, FileText, Clock, CheckCircle, AlertTriangle, DollarSign,
  Calendar, TrendingUp, BarChart3, RefreshCw, Upload, Bot, FileBarChart, Eye,
  IndianRupee
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useDashboard } from "@/hooks/useDashboard";
import { useState, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, PieChart, Pie,
  ResponsiveContainer, Area, AreaChart, Legend, Cell, Tooltip
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrencySymbol } from "@/components/ui/currency-select";


// maps ISO codes -> display prefixes (with disambiguation for "$" currencies)
const currencyPrefix = (c: string | null | undefined): string => {
  if (!c) return "";

  const code = c.toUpperCase();

  const map: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    INR: "₹",
    JPY: "¥",
    CNY: "¥",
    RUB: "₽",
    TRY: "₺",
    KRW: "₩",
    THB: "฿",
    AUD: "A$",
    CAD: "C$",
    NZD: "NZ$",
    HKD: "HK$",
    SGD: "S$",
    MXN: "MX$",
    ARS: "AR$",
    CLP: "CLP$",
    COP: "COP$",
    BRL: "R$",
    CHF: "CHF",
    SEK: "SEK",
    NOK: "NOK",
    DKK: "DKK",
    PLN: "PLN",
    CZK: "CZK",
    HUF: "HUF",
    AED: "AED",
    SAR: "SAR",
    QAR: "QAR",
    ILS: "₪",
    ZAR: "R",
    NGN: "₦",
    IDR: "Rp",
    MYR: "RM",
    PHP: "₱",
    VND: "₫",
    TWD: "NT$",
  };

  if (!/^[A-Za-z]{2,}$/.test(c)) return c; // if backend already gave a symbol

  return map[code] ?? code;
};


const Dashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const {
    stats, statsLoading, statsError,
    deals, dealsLoading, dealsError,
    loading, refetchStats, refetchDeals, refetchAll,
    activityTrend, activityLoading,
    dealTypeTrend, dealTypeLoading,
  } = useDashboard({ limit: 3, search: searchTerm || undefined });

  const { user } = useAuth();
  const isAdminOrAnalyst = user?.role === "ADMIN" || user?.role === "ANALYST";
  const isReviewer = user?.role === "REVIEWER";
  const isViewer = user?.role === "VIEWER";

  // Get organization's default currency and symbol
  const orgCurrency = user?.organization?.defaultCurrency || 'INR';
  const currencySymbol = useMemo(() => getCurrencySymbol(orgCurrency), [orgCurrency]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed": return "bg-green-100 text-green-800";
      case "In Progress": return "bg-blue-100 text-blue-800";
      case "Draft": return "bg-yellow-100 text-yellow-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "High": return "text-red-600";
      case "Medium": return "text-yellow-600";
      case "Low": return "text-green-600";
      default: return "text-gray-600";
    }
  };
  const hasCompletedReport = (deal: any) => deal._count?.reports && deal._count.reports > 0;
  const getCurrentStep = (deal: any) => {
    if (hasCompletedReport(deal)) return 3;
    if (deal.status === "DRAFT") return 1;
    if (deal.status === "ACTIVE") return 1;
    if (deal.status === "DOCUMENTS_SUBMITTED") return 2;
    if (deal.status === "UNDER_REVIEW" || deal.status === "PENDING_APPROVAL") return 2;
    if (deal.status === "COMPLETED") return 3;
    return 1;
  };

  if (statsLoading) {
    return (
      <div className="p-3 sm:p-4 lg:p-6 bg-gradient-to-br from-background via-background to-muted/20 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="relative">
                <RefreshCw className="w-12 h-12 animate-spin text-primary mx-auto" />
                <div className="absolute inset-0 w-12 h-12 border-4 border-primary/20 rounded-full animate-pulse mx-auto" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Loading Dashboard</h3>
                <p className="text-muted-foreground">Fetching your latest data...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (statsError) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <AlertTriangle className="w-12 h-12 text-red-500" />
            <div className="text-center">
              <h3 className="text-lg font-medium text-foreground">Failed to load dashboard</h3>
              <p className="text-muted-foreground">{statsError}</p>
            </div>
            <Button onClick={refetchStats} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const dashboardStats = stats || {
    totalDeals: 0,
    totalDealsChange: 0,
    inProgressDeals: 0,
    completedDeals: 0,
    completedDealsThisQuarter: 0,
    riskAlerts: 0,
    totalValue: "$0",
    totalValueNote: "Combined deal value in USD",
    statusDistribution: { inProgress: 0, completed: 0, draft: 0 },
    riskDistribution: { high: 0, medium: 0, low: 0 },
    stageCounts: { intakeCount: 0, docsSubmittedCount: 0, underReviewCount: 0, approvalCount: 0, completedCount: 0 },
  };

  const activityData =
    activityTrend && activityTrend.length > 0
      ? activityTrend.map((p) => ({ month: p.month, deals: p.deals, value: p.value }))
      : [
          { month: "Jul", deals: 0, value: 0 },
          { month: "Aug", deals: 0, value: 0 },
          { month: "Sep", deals: 0, value: 0 },
          { month: "Oct", deals: 0, value: 0 },
          { month: "Nov", deals: 0, value: 0 },
          { month: "Dec", deals: 0, value: 0 },
        ];

  const stageData = [
    { stage: "Intake", count: dashboardStats.stageCounts.intakeCount },
    { stage: "Docs Submitted", count: dashboardStats.stageCounts.docsSubmittedCount },
    { stage: "Under Review", count: dashboardStats.stageCounts.underReviewCount + dashboardStats.stageCounts.approvalCount },
    { stage: "Completed", count: dashboardStats.stageCounts.completedCount },
  ];

  const typeTrendData =
    dealTypeTrend && dealTypeTrend.length > 0
      ? dealTypeTrend
      : [
          { month: "Jul", MA: 0, DEBT_FINANCING: 0, INVESTMENT: 0 },
          { month: "Aug", MA: 0, DEBT_FINANCING: 0, INVESTMENT: 0 },
          { month: "Sep", MA: 0, DEBT_FINANCING: 0, INVESTMENT: 0 },
          { month: "Oct", MA: 0, DEBT_FINANCING: 0, INVESTMENT: 0 },
          { month: "Nov", MA: 0, DEBT_FINANCING: 0, INVESTMENT: 0 },
          { month: "Dec", MA: 0, DEBT_FINANCING: 0, INVESTMENT: 0 },
        ];

  return (
    <div className="p-6 bg-gradient-to-br from-slate-50 via-background to-slate-100/50 dark:from-slate-900 dark:via-background dark:to-slate-800/50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 lg:space-y-8 page-transition">
        {/* Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-8 text-white shadow-2xl border border-gray-600/50">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(156,163,175,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(156,163,175,0.05),transparent_50%)]" />
          <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-6">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-1.5 sm:w-2 h-6 sm:h-8 bg-gradient-to-b from-gray-400 to-gray-600 rounded-full" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Welcome Back!
                </h1>
              </div>
              <p className="text-gray-300 text-base sm:text-lg font-medium ml-3 sm:ml-5">Here's what's happening with your deals today</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ml-3 sm:ml-5 text-xs sm:text-sm text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span>Live Data</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">
                    {new Date().toLocaleDateString("en-US", {
                      weekday: "long", year: "numeric", month: "long", day: "numeric",
                    })}
                  </span>
                  <span className="sm:hidden">
                    {new Date().toLocaleDateString("en-US", {
                      month: "short", day: "numeric", year: "numeric",
                    })}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <Button
                onClick={refetchAll}
                variant="outline"
                disabled={loading}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border-white/20 hover:border-white/30 backdrop-blur-sm transition-all duration-200 shadow-lg w-full sm:w-auto"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              {user?.role === "ADMIN" && (
                <Link to="/register-deal" className="w-full sm:w-auto">
                  <Button className="flex items-center gap-2 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold w-full sm:w-auto">
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">New Deal</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          <Card className="card-hover border shadow-modern-md bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-900/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-300">Total Deals</CardTitle>
              <div className="p-2 bg-slate-500/10 rounded-lg"><FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" /></div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">{dashboardStats.totalDeals}</div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">+{dashboardStats.totalDealsChange} from last month</p>
            </CardContent>
          </Card>

          <Card className="card-hover border shadow-modern-md bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">In Progress</CardTitle>
              <div className="p-2 bg-gray-500/10 rounded-lg"><Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" /></div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{dashboardStats.inProgressDeals}</div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Active deals</p>
            </CardContent>
          </Card>

          <Card className="card-hover border shadow-modern-md bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Completed</CardTitle>
              <div className="p-2 bg-green-500/10 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" /></div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-900 dark:text-green-100">{dashboardStats.completedDeals}</div>
              <p className="text-sm text-green-600 dark:text-green-400 mt-1">{dashboardStats.completedDealsThisQuarter} this quarter</p>
            </CardContent>
          </Card>

          <Card className="card-hover border shadow-modern-md bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300">Risk Alerts</CardTitle>
              <div className="p-2 bg-red-500/10 rounded-lg"><AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" /></div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-900 dark:text-red-100">{dashboardStats.riskAlerts}</div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">High priority</p>
            </CardContent>
          </Card>

          <Card className="card-hover border shadow-modern-md bg-gradient-to-br from-zinc-50 to-zinc-100 dark:from-zinc-800/50 dark:to-zinc-900/50">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Total Value</CardTitle>
              <div className="p-2 bg-zinc-500/10 rounded-lg"><DollarSign className="h-5 w-5 text-zinc-600 dark:text-zinc-400" /></div>
            </CardHeader>
            <CardContent>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 cursor-pointer">
                      {currencySymbol} {formatDealValue(Number(dashboardStats.totalValue), orgCurrency)}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{currencySymbol}{formatFullValue(Number(dashboardStats.totalValue), orgCurrency)}</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">Combined value ({orgCurrency})</p>
            </CardContent>
          </Card>
        </div>

        {/* Status % + Risk % */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-stretch">
          <Card className="card-hover border-0 shadow-modern-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500/10 rounded-lg"><BarChart3 className="h-5 w-5 text-slate-600" /></div>
                <div>
                  <CardTitle className="text-lg font-semibold">Deal Status Distribution</CardTitle>
                  <CardDescription>Current status of all deals</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="min-w-0 p-3 sm:p-6">
              <div className="w-full h-[200px] sm:h-[250px] lg:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: "In Progress", value: dashboardStats.statusDistribution.inProgress, fill: "#64748b" },
                      { name: "Completed", value: dashboardStats.statusDistribution.completed, fill: "#10b981" },
                      { name: "Draft", value: dashboardStats.statusDistribution.draft, fill: "#f59e0b" },
                    ]}
                    margin={{ top: 10, right: 5, left: 5, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} height={60} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v}%`} />
                    <Tooltip formatter={(value: number, name: string) => [`${value}%`, name]} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover border-0 shadow-modern-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-500/10 rounded-lg"><AlertTriangle className="h-5 w-5 text-slate-600" /></div>
                <div>
                  <CardTitle className="text-lg font-semibold">Risk Overview</CardTitle>
                  <CardDescription>Risk distribution across deals</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="min-w-0 p-3 sm:p-6">
              <div className="w-full h-[200px] sm:h-[250px] lg:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip formatter={(value: number, name: string) => [`${value}%`, name]} />
                    <Pie
                      data={[
                        { name: "High Risk", value: dashboardStats.riskDistribution.high },
                        { name: "Medium Risk", value: dashboardStats.riskDistribution.medium },
                        { name: "Low Risk", value: dashboardStats.riskDistribution.low },
                      ]}
                      cx="50%" cy="50%" outerRadius={80} dataKey="value"
                    >
                      <Cell fill="#ef4444" /><Cell fill="#f59e0b" /><Cell fill="#10b981" />
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity + Deal Types */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-stretch">
          <Card className="card-hover border-0 shadow-modern-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg"><TrendingUp className="h-5 w-5 text-blue-600" /></div>
                <div>
                  <CardTitle className="text-lg font-semibold">Deal Activity Trend</CardTitle>
                  <CardDescription>Monthly deal creation over the last 6 months</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="min-w-0 p-3 sm:p-6">
              <div className="w-full h-[200px] sm:h-[250px] lg:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activityData} margin={{ top: 10, right: 5, left: 5, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} tickMargin={4} />
                    <YAxis tick={{ fontSize: 10 }} tickMargin={4} domain={[0, "dataMax + 10"]} />
                    <Tooltip />
                    <Area type="monotone" dataKey="deals" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              {activityLoading && <div className="text-xs text-muted-foreground mt-2">Updating…</div>}
            </CardContent>
          </Card>

          <Card className="card-hover border-0 shadow-modern-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500/10 rounded-lg"><BarChart3 className="h-5 w-5 text-violet-600" /></div>
                <div>
                  <CardTitle className="text-lg font-semibold">Deal Types by Month</CardTitle>
                  <CardDescription>M&A vs Debt Financing vs Investment (last 6 months)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="min-w-0 p-3 sm:p-6">
              <div className="w-full h-[200px] sm:h-[250px] lg:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={typeTrendData} margin={{ top: 10, right: 5, left: 5, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    <Bar dataKey="MA" name="M&A" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="DEBT_FINANCING" name="Debt Financing" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="INVESTMENT" name="Investment" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {dealTypeLoading && <div className="text-xs text-muted-foreground mt-2">Updating…</div>}
            </CardContent>
          </Card>
        </div>

        {/* Stage Distribution */}
        <div className="grid grid-cols-1">
          <Card className="card-hover border-0 shadow-modern-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-500/10 rounded-lg"><BarChart3 className="h-5 w-5 text-indigo-600" /></div>
                <div>
                  <CardTitle className="text-lg font-semibold">Stage Distribution</CardTitle>
                  <CardDescription>Current deals by stage</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="min-w-0 p-3 sm:p-6">
              <div className="w-full h-[200px] sm:h-[250px] lg:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stageData} margin={{ top: 10, right: 5, left: 5, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" tick={{ fontSize: 10 }} interval={0} height={60} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Deals */}
        <Card className="border-0 shadow-modern-md">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold">Recent Deal Activity</CardTitle>
                <CardDescription>Latest updates from your active deals</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search deals..."
                    className="pl-10 w-full border-2 focus:border-primary/50 transition-colors"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <Link to="/deals" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto flex items-center gap-2 hover:bg-primary hover:text-primary-foreground transition-colors">
                  <FileText className="w-4 h-4" />
                  View All Deals
                </Button>
              </Link>
            </div>

            {dealsLoading && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin" />
                <span className="ml-2">Loading recent deals...</span>
              </div>
            )}

            {dealsError && (
              <div className="text-center py-8 text-red-500">
                <p>Error loading recent deals: {dealsError}</p>
                <Button onClick={refetchDeals} variant="outline" className="mt-2">
                  Try Again
                </Button>
              </div>
            )}

            {!dealsLoading && !dealsError && (
              <div className="space-y-4">
                {deals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No recent deals found.</p>
                  </div>
                ) : (
                  deals.map((deal) => {
                    const formatValue = (value: number | null, currency: string | null) => {
                      if (value == null) return "N/A";
                      const prefix = currencyPrefix(currency);
                      const sep = prefix ? " " : ""; // always add space if we have a prefix
                      if (value >= 1_000_000_000) return `${prefix}${sep}${(value / 1_000_000_000).toFixed(1)}B`;
                    if (value >= 1_000_000)     return `${prefix}${sep}${(value / 1_000_000).toFixed(1)}M`;
                    if (value >= 1_000)         return `${prefix}${sep}${(value / 1_000).toFixed(1)}K`;
                    return `${prefix}${sep}${value.toFixed(0)}`;
                  };


                    const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : "No date set");

                    const onOpenDeal = () => navigate(`/deals/${deal.id}`);
                    const onKeyOpen: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpenDeal();
                      }
                    };

                    return (
                      <div
                        key={deal.id}
                        role="button"
                        tabIndex={0}
                        onClick={onOpenDeal}
                        onKeyDown={onKeyOpen}
                        className="relative group cursor-pointer border-0 rounded-xl p-6 bg-card shadow-modern-sm hover:shadow-modern-md transition-all duration-300 card-hover focus:outline-none focus:ring-2 focus:ring-primary/50"
                      >

                        <div className="flex flex-col gap-4">
                          <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                              <h3 className="font-semibold text-lg group-hover:text-primary transition-colors truncate">{deal.title}</h3>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge className={`${getStatusColor(deal.status)} shadow-sm text-xs`}>{deal.status}</Badge>
                                <Badge variant="outline" className="border-2 text-xs">{deal.type}</Badge>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <span className="font-light">{formatValue(deal.value, deal.currency)}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                <span className="font-light">Expected: {formatDate(deal.expectedClosingDate)}</span>
                              </div>
                              {deal.riskLevel && (
                                <div className="flex items-center gap-1">
                                  <AlertTriangle className={`w-4 h-4 ${getRiskColor(deal.riskLevel)}`} />
                                  <span className="font-light">{deal.riskLevel} Risk</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4" />
                                <span className="font-light">{deal.documentsCount} docs</span>
                              </div>
                            </div>
                          </div>

                          <div className="w-full">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="font-light">Progress</span>
                              <span className="font-light">{deal.progress || 0}%</span>
                            </div>
                            <Progress value={deal.progress || 0} className="w-full" />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default Dashboard;
